import React from 'react';
import {
  FaCheckCircle,
  FaChevronRight,
  FaCog,
  FaImage,
  FaLayerGroup,
  FaLock,
  FaPalette,
  FaRegCircle,
  FaShieldAlt,
} from 'react-icons/fa';
import { getCompletedModuleCount } from '../../services/moduleCompletion';

const PANEL_META = {
  theme: {
    label: 'Theme',
    detail: 'Style, colors, and opening rhythm',
    icon: FaPalette,
  },
  cover: {
    label: 'Opening Cover',
    detail: 'Recipient intro and 9:16 cover photo',
    icon: FaImage,
  },
  modules: {
    label: 'Chapters',
    detail: 'Edit one module at a time',
    icon: FaLayerGroup,
  },
  privacy: {
    label: 'Share',
    detail: 'Who can open or share this gift',
    icon: FaShieldAlt,
  },
  publish: {
    label: 'Publish',
    detail: 'Preview, draft, and seal',
    icon: FaLock,
  },
  settings: {
    label: 'Settings',
    detail: 'Password, NFC members, and policies',
    icon: FaCog,
  },
};

const getThemeLabel = (pageData = {}) => {
  const raw = pageData.templateName || pageData.themeName || pageData.templateKey || pageData.presetKey || pageData.themeId;
  if (!raw) return 'Choose';
  return String(raw)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getCoverReady = (pageData = {}) => Boolean(
  pageData.openingCover?.imageUrl ||
  pageData.coverImageUrl ||
  pageData.ownerNameTouched ||
  pageData.openingTitleTouched ||
  pageData.openingSubtitleTouched
);

const getPrivacyLabel = (pageData = {}) => {
  const mode = pageData.sharingMode || pageData.sharing?.mode || pageData.privacyMode || 'link';
  if (mode === 'off') return 'Sharing Off';
  if (mode === 'card') return 'Card Only';
  return 'Link Access';
};

const GiftEditorOverview = ({ pageData, theme, isLocked, onPanelChange }) => {
  const modules = Array.isArray(pageData?.modules) ? pageData.modules : [];
  const completeCount = getCompletedModuleCount(modules);
  const coverReady = getCoverReady(pageData);
  const rows = [
    {
      id: 'cover',
      ...PANEL_META.cover,
      status: coverReady ? 'Ready' : 'Missing',
      ready: coverReady,
    },
    {
      id: 'theme',
      ...PANEL_META.theme,
      status: getThemeLabel(pageData),
      ready: true,
    },
    {
      id: 'modules',
      ...PANEL_META.modules,
      status: `${completeCount}/${modules.length || 0} ready`,
      ready: modules.length > 0 && completeCount === modules.length,
    },
    {
      id: 'privacy',
      ...PANEL_META.privacy,
      status: getPrivacyLabel(pageData),
      ready: true,
    },
    {
      id: 'publish',
      ...PANEL_META.publish,
      status: isLocked ? 'Sealed' : 'Draft',
      ready: isLocked,
    },
    {
      id: 'settings',
      ...PANEL_META.settings,
      status: 'Manage',
      ready: true,
    },
  ];

  return (
    <section className="lumirae-editor-panel lumirae-editor-overview p-5">
      <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
        Gift Builder
      </div>
      <h2 className="mt-2 text-2xl font-black tracking-tight" style={{ color: theme.text }}>
        Choose what to edit
      </h2>
      <p className="mt-2 text-xs font-medium leading-relaxed opacity-60">
        Each section opens as one focused editor, so you do not need to scroll through the whole setup page.
      </p>

      <div className="mt-5 grid gap-3">
        {rows.map((row) => {
          const Icon = row.icon;
          return (
            <button
              key={row.id}
              type="button"
              className="lumirae-editor-overview-row"
              onClick={() => onPanelChange?.(row.id)}
            >
              <span className="lumirae-editor-overview-icon">
                <Icon />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block text-[10px] font-black uppercase tracking-widest" style={{ color: theme.text }}>
                  {row.label}
                </span>
                <span className="mt-1 block truncate text-[10px] font-medium opacity-55">
                  {row.detail}
                </span>
              </span>
              <span className={`lumirae-editor-overview-status ${row.ready ? 'is-ready' : 'is-missing'}`}>
                {row.ready ? <FaCheckCircle /> : <FaRegCircle />}
                {row.status}
              </span>
              <FaChevronRight className="text-[10px] opacity-35" />
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default GiftEditorOverview;
